import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import ProfilePage from './pages/ProfilePage';
import HomePage from './pages/HomePage';
import CalendarPage from './pages/CalendarPage';
import SubEventDetailPage from './pages/SubEventDetailPage';
import TournamentsResultsPage from './pages/TournamentsResultsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import VenuesMapPage from './pages/VenuesMapPage';
import MyParticipationsPage from './pages/MyParticipationsPage';
import MySchedulePage from './pages/MySchedulePage';
import Service1Page from './pages/services/Service1Page';
import Service2Page from './pages/services/Service2Page';
import Service3Page from './pages/services/Service3Page';

// Nouvelles pages de gestion des festivals
import ManageFestivalsPage from './pages/ManageFestivalsPage';
import CreateEditFestivalPage from './pages/CreateEditFestivalPage';
import ManageTournamentsPage from './pages/ManageTournamentsPage';
import ManageSeriesPage from './pages/ManageSeriesPage';
import ImportFestivalsPage from './pages/ImportFestivalsPage';
import CreateEditTournamentPage from './pages/CreateEditTournamentPage';

// Interface d'administration extensible
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManageVenuesPage from './pages/admin/ManageVenuesPage';
import CreateEditVenuePage from './pages/admin/CreateEditVenuePage';
import ManageCircuitsPage from './pages/admin/ManageCircuitsPage';
import CreateEditCircuitPage from './pages/admin/CreateEditCircuitPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import ManageParticipationsPage from './pages/admin/ManageParticipationsPage';

// Composant pour protéger les routes (nécessite connexion)
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, mustChangePassword, isLoading } = useAuth();
  const location = useLocation();

  // Tant que la session n'a pas fini d'être restaurée depuis le storage
  // (juste après un rafraîchissement de page), isLoggedIn vaut encore
  // "false" par défaut : il ne faut pas rediriger vers /login sur la base
  // de cette valeur provisoire, sous peine de déconnecter des utilisateurs
  // pourtant bien connectés.
  if (isLoading) {
    return null;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // Force le changement de mot de passe suite à une réinitialisation admin
  // avant d'accéder à n'importe quelle autre page protégée.
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return children;
};

// Composant pour les routes admin uniquement
const AdminRoute = ({ children }) => {
  const { isLoggedIn, userRole, mustChangePassword, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
          <p className="text-gray-600 mb-6">Vous n'avez pas les droits d'administration nécessaires.</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Routes publiques */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Routes protégées */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/event/:eventId/subevent/:subEventId"
            element={
              <ProtectedRoute>
                <SubEventDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <LeaderboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-schedule"
            element={
              <ProtectedRoute>
                <MySchedulePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-participations"
            element={
              <ProtectedRoute>
                <MyParticipationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/venues-map"
            element={
              <ProtectedRoute>
                <VenuesMapPage />
              </ProtectedRoute>
            }
          />

          {/* Routes admin - Nouvelle gestion des festivals */}
          <Route
            path="/manage-festivals"
            element={
              <AdminRoute>
                <ManageFestivalsPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/import"
            element={
              <AdminRoute>
                <ImportFestivalsPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/create"
            element={
              <AdminRoute>
                <CreateEditFestivalPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/edit/:festivalId"
            element={
              <AdminRoute>
                <CreateEditFestivalPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/:festivalId/tournaments"
            element={
              <AdminRoute>
                <ManageTournamentsPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/:festivalId/series"
            element={
              <AdminRoute>
                <ManageSeriesPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/:festivalId/tournaments/create"
            element={
              <AdminRoute>
                <CreateEditTournamentPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/:festivalId/tournaments/edit/:tournamentId"
            element={
              <AdminRoute>
                <CreateEditTournamentPage />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-festivals/:festivalId/tournaments/:tournamentId/participants"
            element={
              <AdminRoute>
                <ManageParticipationsPage />
              </AdminRoute>
            }
          />

          {/* Routes admin - Tableau de bord d'administration extensible */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/venues"
            element={
              <AdminRoute>
                <ManageVenuesPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/venues/create"
            element={
              <AdminRoute>
                <CreateEditVenuePage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/venues/edit/:venueId"
            element={
              <AdminRoute>
                <CreateEditVenuePage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/circuits"
            element={
              <AdminRoute>
                <ManageCircuitsPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/circuits/create"
            element={
              <AdminRoute>
                <CreateEditCircuitPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/circuits/edit/:circuitId"
            element={
              <AdminRoute>
                <CreateEditCircuitPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <ManageUsersPage />
              </AdminRoute>
            }
          />

          {/* Route des résultats de tournois (consultation uniquement :
              la saisie se fait sur /my-participations) */}
          <Route
            path="/results"
            element={
              <ProtectedRoute>
                <TournamentsResultsPage />
              </ProtectedRoute>
            }
          />

          {/* Routes des services */}
          <Route
            path="/service/1"
            element={
              <ProtectedRoute>
                <Service1Page />
              </ProtectedRoute>
            }
          />

          <Route
            path="/service/2"
            element={
              <ProtectedRoute>
                <Service2Page />
              </ProtectedRoute>
            }
          />

          <Route
            path="/service/3"
            element={
              <ProtectedRoute>
                <Service3Page />
              </ProtectedRoute>
            }
          />

          {/* Redirection par défaut */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
